/**
 * 歌词竖排排布：沿屏幕右上边缘竖排，按佛像剪影（含头部圆光）逐行避让。
 *
 * - 长句按标点分成短语，每个短语起一列（从右往左读）；短语过长时再均匀折列；
 * - 先在右侧找能放下的最大字号；若右侧只能用很小的字号，改用「对联式」——
 *   前半句在右、后半句在左，两侧对称排在佛像两边；
 * - 列的上下范围决定需要避让的剪影宽度，因此会同时尝试「少列长列」与「多列短列」。
 */

export interface LyricEnv {
  vw: number;
  vh: number;
  /** 歌词区顶端 / 底端（px） */
  top: number;
  bottom: number;
  /** 距屏幕左右边缘的距离（px） */
  margin: number;
  /** [y0, y1] 范围内需要避让的主体左右边界（px） */
  extent: (y0: number, y1: number) => { left: number; right: number };
  /** 期望字号 / 最小字号（px） */
  fPref: number;
  fMin: number;
  /** 字距（em） */
  spacing: number;
  /** 'right' 右上边缘（必要时自动改为对联）；'couplet' 两侧对联 */
  mode: 'right' | 'couplet';
}

export interface LyricBlock {
  /** right 侧：块的右边缘 x；left 侧：块的左边缘 x */
  x: number;
  y: number;
  cols: string[];
  /** 块宽高（px） */
  w: number;
  h: number;
}

export interface LyricPlan {
  f: number;
  /** 每字纵向步进 / 列距（px） */
  adv: number;
  pitch: number;
  right: LyricBlock;
  left: LyricBlock | null;
  /** 与主体之间的分界线 x（右块左侧 / 左块右侧） */
  gap: number;
  /** 空间不足、退化到最小字号且可能贴近主体 */
  cramped: boolean;
}

/** 列内断开的标点（断开处不显示该标点） */
const BREAK = /[，,、；;：:。．.\s]+/;

/** 按标点切成短语；句末的 ！？ 保留在短语末尾 */
export function splitPhrases(text: string): string[] {
  return text
    .split(BREAK)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** 把每个短语按每列最多 perCol 字均匀折列；只剩句末标点时并入上一列 */
export function wrapPhrases(phrases: string[], perCol: number): string[] {
  const out: string[] = [];
  for (const p of phrases) {
    const chars = [...p];
    const n = chars.length;
    const cols = Math.max(1, Math.ceil(n / perCol));
    const per = Math.ceil(n / cols);
    const segs: string[] = [];
    for (let i = 0; i < n; i += per) segs.push(chars.slice(i, i + per).join(''));
    if (segs.length > 1 && /^[！？!?」』）)]+$/.test(segs[segs.length - 1])) segs[segs.length - 2] += segs.pop();
    out.push(...segs);
  }
  return out;
}

const len = (s: string) => [...s].length;

interface Fit {
  cols: string[];
  h: number;
  w: number;
  x: number;
}

function fitSide(side: 'right' | 'left', phrases: string[], adv: number, pitch: number, gap: number, env: LyricEnv): Fit | null {
  if (!phrases.length) return null;
  const maxLen = Math.max(...phrases.map(len));
  const maxPerCol = Math.floor((env.bottom - env.top) / adv);
  if (maxPerCol < 2) return null;
  const hi = Math.min(maxLen, maxPerCol);
  const lo = Math.max(2, Math.ceil(maxLen / 4));
  for (let per = hi; per >= Math.min(lo, hi); per--) {
    const cols = wrapPhrases(phrases, per);
    const h = Math.max(...cols.map(len)) * adv;
    const w = cols.length * pitch;
    const ext = env.extent(env.top, env.top + h);
    if (side === 'right') {
      const avail = env.vw - env.margin - (ext.right + gap);
      if (w <= avail) return { cols, h, w, x: env.vw - env.margin };
    } else {
      const avail = ext.left - gap - env.margin;
      if (w <= avail) return { cols, h, w, x: env.margin };
    }
  }
  return null;
}

/** 对联式拆分：按字数尽量平均地把短语分成前后两组；只有一个短语时从中间断开 */
export function splitCouplet(phrases: string[]): [string[], string[]] {
  if (phrases.length === 1) {
    const c = [...phrases[0]];
    const k = Math.ceil(c.length / 2);
    return [[c.slice(0, k).join('')], [c.slice(k).join('')]];
  }
  const total = phrases.reduce((a, p) => a + len(p), 0);
  let acc = 0;
  let best = 1;
  let bestDiff = Infinity;
  for (let i = 1; i < phrases.length; i++) {
    acc += len(phrases[i - 1]);
    const diff = Math.abs(total - 2 * acc);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  }
  return [phrases.slice(0, best), phrases.slice(best)];
}

export function planLyrics(text: string, env: LyricEnv): LyricPlan {
  const phrases = splitPhrases(text);
  if (!phrases.length) phrases.push(text);
  const total = phrases.reduce((a, p) => a + len(p), 0);
  const geom = (f: number) => ({ adv: f * (1 + env.spacing), pitch: f * 1.55, gap: Math.max(14, f * 0.75) });

  let right: { f: number; fit: Fit } | null = null;
  let couplet: { f: number; r: Fit; l: Fit } | null = null;
  const canCouplet = phrases.length >= 2 || total >= 9;
  const [pr, pl] = splitCouplet(phrases);
  // 对联模式下的短句（如四字、六字佛号）仍然只排在右侧
  const wantRight = env.mode === 'right' || !canCouplet;

  for (let f = Math.round(env.fPref); f >= env.fMin; f -= Math.max(1, Math.round(f * 0.04))) {
    const g = geom(f);
    if (!right && wantRight) {
      const fit = fitSide('right', phrases, g.adv, g.pitch, g.gap, env);
      if (fit) right = { f, fit };
    }
    if (!couplet && canCouplet) {
      const r = fitSide('right', pr, g.adv, g.pitch, g.gap, env);
      const l = r && fitSide('left', pl, g.adv, g.pitch, g.gap, env);
      if (r && l) couplet = { f, r, l };
    }
    if ((right || !wantRight) && (couplet || !canCouplet)) break;
  }

  const useCouplet = couplet && (env.mode === 'couplet' || !right || right.f < couplet.f * 0.8);
  if (useCouplet && couplet) {
    const g = geom(couplet.f);
    const h = Math.max(couplet.r.h, couplet.l.h);
    return {
      f: couplet.f,
      ...g,
      right: { x: couplet.r.x, y: env.top, cols: couplet.r.cols, w: couplet.r.w, h },
      left: { x: couplet.l.x, y: env.top, cols: couplet.l.cols, w: couplet.l.w, h },
      cramped: false,
    };
  }
  if (right) {
    const g = geom(right.f);
    return { f: right.f, ...g, right: { x: right.fit.x, y: env.top, cols: right.fit.cols, w: right.fit.w, h: right.fit.h }, left: null, cramped: false };
  }
  // 空间极小：最小字号、尽量短列，贴右边缘
  const f = env.fMin;
  const g = geom(f);
  const per = Math.max(2, Math.floor((env.bottom - env.top) / g.adv));
  const cols = wrapPhrases(phrases, per);
  return {
    f,
    ...g,
    right: { x: env.vw - env.margin, y: env.top, cols, w: cols.length * g.pitch, h: Math.max(...cols.map(len)) * g.adv },
    left: null,
    cramped: true,
  };
}
